'use client';

import React, { useState, useMemo } from 'react';
import SignalCard from './SignalCard';

import { TrendingUp, Zap, CheckCircle2, Clock } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export interface Signal {
  id: string;
  keyword: string;
  topic: string;
  platform: 'reddit' | 'hn' | 'producthunt';
  subreddit?: string;
  score: number;
  postCount: number;
  commentCount: number;
  velocity24h: number;
  status: 'used' | 'unused';
  linkedScript?: string;
  linkedScriptId?: string;
  capturedAt: string;
  topPosts: { title: string; upvotes: number; url: string }[];
  velocityHistory: number[];
  keywords: string[];
}

const signals: Signal[] = [
  {
    id: 'signal-001',
    keyword: 'claude-sonnet-coding',
    topic: 'Claude 3.5 Sonnet coding performance vs GPT-4o',
    platform: 'reddit',
    subreddit: 'r/artificial',
    score: 94,
    postCount: 847,
    commentCount: 12400,
    velocity24h: 312,
    status: 'used',
    linkedScript: 'Claude 3.5 Sonnet vs GPT-4o: Which AI Writes Better Code?',
    linkedScriptId: 'script-001',
    capturedAt: '2026-07-30 07:12',
    topPosts: [
      { title: 'Claude 3.5 Sonnet now beats GPT-4o on SWE-bench — detailed analysis', upvotes: 4820, url: '#' },
      { title: 'I switched my entire dev workflow to Claude — here\'s 30 days of data', upvotes: 2340, url: '#' },
      { title: 'Anthropic quietly updated Sonnet — it\'s now dramatically better at refactoring', upvotes: 1870, url: '#' },
    ],
    velocityHistory: [28, 41, 67, 89, 134, 198, 245, 312],
    keywords: ['claude', 'sonnet', 'coding', 'gpt-4o', 'benchmark'],
  },
  {
    id: 'signal-002',
    keyword: 'openai-o3-release',
    topic: 'OpenAI o3 reasoning model release and benchmarks',
    platform: 'hn',
    score: 91,
    postCount: 623,
    commentCount: 8900,
    velocity24h: 287,
    status: 'used',
    linkedScript: 'OpenAI o3 Reasoning Model Benchmarks Explained in 50s',
    linkedScriptId: 'script-002',
    capturedAt: '2026-07-29 07:08',
    topPosts: [
      { title: 'OpenAI o3 achieves 87.5% on ARC-AGI — what this actually means', upvotes: 3240, url: '#' },
      { title: 'Show HN: I ran o3 on 200 logic puzzles — the results are wild', upvotes: 1890, url: '#' },
      { title: 'o3 vs o1: A systematic comparison of reasoning chains', upvotes: 1420, url: '#' },
    ],
    velocityHistory: [45, 78, 112, 167, 210, 256, 271, 287],
    keywords: ['o3', 'reasoning', 'openai', 'benchmark', 'arc-agi'],
  },
  {
    id: 'signal-003',
    keyword: 'gemini-2-flash-demo',
    topic: 'Google Gemini 2.0 Flash multimodal real-time capabilities',
    platform: 'reddit',
    subreddit: 'r/artificial',
    score: 88,
    postCount: 541,
    commentCount: 7200,
    velocity24h: 241,
    status: 'unused',
    capturedAt: '2026-07-30 08:15',
    topPosts: [
      { title: 'Gemini 2.0 Flash processes video + audio + text simultaneously — demo thread', upvotes: 3120, url: '#' },
      { title: 'Google just made Gemini 2.0 Flash free for all developers', upvotes: 2480, url: '#' },
      { title: 'Comparing Gemini 2.0 Flash latency to GPT-4o-mini on 500 tasks', upvotes: 1340, url: '#' },
    ],
    velocityHistory: [12, 34, 58, 92, 141, 187, 218, 241],
    keywords: ['gemini', 'flash', 'multimodal', 'google', 'real-time'],
  },
  {
    id: 'signal-004',
    keyword: 'ai-agents-production-2026',
    topic: 'AI agents moving from demos to production in enterprise',
    platform: 'hn',
    score: 85,
    postCount: 412,
    commentCount: 6100,
    velocity24h: 198,
    status: 'unused',
    capturedAt: '2026-07-30 09:02',
    topPosts: [
      { title: 'Why 84% of AI agent demos fail to reach production — a post-mortem', upvotes: 2890, url: '#' },
      { title: 'We deployed AI agents to 40,000 users — what actually broke', upvotes: 1670, url: '#' },
      { title: 'The agent reliability problem is worse than anyone is admitting', upvotes: 1240, url: '#' },
    ],
    velocityHistory: [8, 22, 47, 78, 112, 156, 178, 198],
    keywords: ['agents', 'production', 'enterprise', 'reliability', 'deployment'],
  },
  {
    id: 'signal-005',
    keyword: 'cursor-vs-copilot-2026',
    topic: 'Cursor AI vs GitHub Copilot developer productivity comparison',
    platform: 'reddit',
    subreddit: 'r/programming',
    score: 82,
    postCount: 389,
    commentCount: 5400,
    velocity24h: 174,
    status: 'unused',
    capturedAt: '2026-07-30 09:45',
    topPosts: [
      { title: 'I tracked my productivity switching from Copilot to Cursor for 30 days', upvotes: 2340, url: '#' },
      { title: 'Cursor just added multi-file context — Copilot is now behind on this', upvotes: 1890, url: '#' },
      { title: 'The real cost of Cursor vs Copilot at 10-person team scale', upvotes: 980, url: '#' },
    ],
    velocityHistory: [14, 31, 54, 82, 118, 147, 161, 174],
    keywords: ['cursor', 'copilot', 'productivity', 'ide', 'developer'],
  },
  {
    id: 'signal-006',
    keyword: 'sd35-dalle3-comparison',
    topic: 'Stable Diffusion 3.5 vs DALL-E 3 image quality comparison',
    platform: 'producthunt',
    score: 79,
    postCount: 312,
    commentCount: 4200,
    velocity24h: 156,
    status: 'unused',
    capturedAt: '2026-07-30 10:12',
    topPosts: [
      { title: 'SD 3.5 achieves DALL-E 3 photorealism at 1/10th the API cost — benchmark results', upvotes: 1920, url: '#' },
      { title: 'I generated 1000 images with both models — the quality gap is closing fast', upvotes: 1340, url: '#' },
      { title: 'SD 3.5 outperforms DALL-E 3 on text rendering — examples inside', upvotes: 870, url: '#' },
    ],
    velocityHistory: [6, 18, 42, 71, 103, 131, 144, 156],
    keywords: ['stable-diffusion', 'dalle', 'image-gen', 'comparison', 'api-cost'],
  },
  {
    id: 'signal-007',
    keyword: 'rlhf-hallucination-reduction',
    topic: 'New RLHF technique reducing LLM hallucination rates by 41%',
    platform: 'hn',
    score: 87,
    postCount: 298,
    commentCount: 4800,
    velocity24h: 189,
    status: 'used',
    linkedScript: 'LLM Hallucination Rate Drop: New RLHF Technique Breakdown',
    linkedScriptId: 'script-005',
    capturedAt: '2026-07-30 07:22',
    topPosts: [
      { title: 'Paper: Constitutional RLHF reduces factual hallucinations by 41.3% on TruthfulQA', upvotes: 2760, url: '#' },
      { title: 'The training trick that\'s making LLMs dramatically more reliable', upvotes: 1580, url: '#' },
      { title: 'We implemented this RLHF variant in production — 6-month results', upvotes: 1120, url: '#' },
    ],
    velocityHistory: [21, 44, 72, 108, 142, 167, 178, 189],
    keywords: ['rlhf', 'hallucination', 'training', 'reliability', 'truthfulqa'],
  },
  {
    id: 'signal-008',
    keyword: 'llm-function-calling-tools',
    topic: 'LLM function calling and tool use patterns for AI agents',
    platform: 'reddit',
    subreddit: 'r/MachineLearning',
    score: 76,
    postCount: 267,
    commentCount: 3900,
    velocity24h: 134,
    status: 'used',
    linkedScript: 'Function Calling in LLMs: The Tool Use Pattern Explained',
    linkedScriptId: 'script-012',
    capturedAt: '2026-07-30 08:44',
    topPosts: [
      { title: 'The definitive guide to function calling patterns across GPT-4, Claude, Gemini', upvotes: 1840, url: '#' },
      { title: 'Tool use vs function calling — they\'re not the same thing', upvotes: 1240, url: '#' },
      { title: 'Building reliable tool-use agents: lessons from 6 months in production', upvotes: 890, url: '#' },
    ],
    velocityHistory: [9, 24, 48, 74, 98, 118, 127, 134],
    keywords: ['function-calling', 'tool-use', 'agents', 'openai', 'claude'],
  },
  {
    id: 'signal-009',
    keyword: 'mistral-llama-edge',
    topic: 'Mistral 7B vs Llama 3.1 for edge and local deployment',
    platform: 'producthunt',
    score: 74,
    postCount: 241,
    commentCount: 3400,
    velocity24h: 118,
    status: 'used',
    linkedScript: 'Mistral 7B vs Llama 3.1: Edge Deployment Comparison',
    linkedScriptId: 'script-006',
    capturedAt: '2026-07-30 07:28',
    topPosts: [
      { title: 'Running Mistral 7B on M3 MacBook Pro — real performance numbers', upvotes: 1680, url: '#' },
      { title: 'Llama 3.1 8B vs Mistral 7B: edge inference benchmark suite', upvotes: 1140, url: '#' },
      { title: 'The quantization guide for local LLM deployment in 2026', upvotes: 780, url: '#' },
    ],
    velocityHistory: [7, 19, 38, 61, 84, 102, 111, 118],
    keywords: ['mistral', 'llama', 'edge', 'local', 'inference'],
  },
  {
    id: 'signal-010',
    keyword: 'anthropic-constitutional-ai',
    topic: 'Anthropic Constitutional AI training methodology deep dive',
    platform: 'hn',
    score: 83,
    postCount: 198,
    commentCount: 3100,
    velocity24h: 142,
    status: 'used',
    linkedScript: 'Anthropic Constitutional AI — What It Actually Means',
    linkedScriptId: 'script-004',
    capturedAt: '2026-07-30 07:18',
    topPosts: [
      { title: 'Anthropic publishes full Constitutional AI training methodology — annotated summary', upvotes: 2140, url: '#' },
      { title: 'Why Constitutional AI is different from RLHF — a technical breakdown', upvotes: 1480, url: '#' },
      { title: 'Testing Claude\'s constitution: 200 edge cases where it refused me', upvotes: 940, url: '#' },
    ],
    velocityHistory: [18, 38, 64, 91, 113, 129, 136, 142],
    keywords: ['constitutional-ai', 'anthropic', 'alignment', 'claude', 'training'],
  },
  {
    id: 'signal-011',
    keyword: 'finetuning-vs-rag',
    topic: 'Fine-tuning vs RAG decision framework for production LLMs',
    platform: 'reddit',
    subreddit: 'r/LocalLLaMA',
    score: 71,
    postCount: 178,
    commentCount: 2800,
    velocity24h: 98,
    status: 'used',
    linkedScript: 'Fine-Tuning vs RAG: When Each Approach Actually Wins',
    linkedScriptId: 'script-009',
    capturedAt: '2026-07-30 08:58',
    topPosts: [
      { title: 'I\'ve built 40 LLM apps — here\'s when I use RAG vs fine-tuning', upvotes: 1760, url: '#' },
      { title: 'RAG is being overused — a cost analysis of when fine-tuning wins', upvotes: 1240, url: '#' },
      { title: 'The decision tree I use for every new LLM project', upvotes: 840, url: '#' },
    ],
    velocityHistory: [5, 14, 28, 48, 67, 82, 91, 98],
    keywords: ['rag', 'fine-tuning', 'llm', 'production', 'cost'],
  },
  {
    id: 'signal-012',
    keyword: 'perplexity-deep-research',
    topic: 'Perplexity AI Deep Research feature vs Google Gemini Research',
    platform: 'reddit',
    subreddit: 'r/artificial',
    score: 89,
    postCount: 512,
    commentCount: 7800,
    velocity24h: 224,
    status: 'used',
    linkedScript: 'Perplexity AI Deep Research vs Gemini: 3 Key Differences',
    linkedScriptId: 'script-003',
    capturedAt: '2026-07-28 07:10',
    topPosts: [
      { title: 'Perplexity Deep Research vs Gemini Deep Research — 50 query comparison', upvotes: 2980, url: '#' },
      { title: 'I replaced Google Scholar with Perplexity Deep Research for 2 weeks', upvotes: 2140, url: '#' },
      { title: 'The citation accuracy gap between Perplexity and Gemini is getting smaller', upvotes: 1320, url: '#' },
    ],
    velocityHistory: [32, 58, 91, 132, 168, 198, 212, 224],
    keywords: ['perplexity', 'deep-research', 'gemini', 'search', 'citations'],
  },
];

type PlatformFilter = 'all' | 'reddit' | 'hn' | 'producthunt';
type StatusFilter = 'all' | 'used' | 'unused';
type SortBy = 'score' | 'velocity24h' | 'postCount' | 'capturedAt';

const platformTabs: { label: string; value: PlatformFilter; color: string }[] = [
  { label: 'All Sources', value: 'all', color: '' },
  { label: 'Reddit', value: 'reddit', color: 'text-orange-400' },
  { label: 'Hacker News', value: 'hn', color: 'text-amber-400' },
  { label: 'Product Hunt', value: 'producthunt', color: 'text-red-400' },
];

export default function TrendFeedClient() {
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('score');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let result = [...signals];
    if (platformFilter !== 'all') result = result.filter((s) => s.platform === platformFilter);
    if (statusFilter !== 'all') result = result.filter((s) => s.status === statusFilter);
    result.sort((a, b) => {
      if (sortBy === 'capturedAt') return b.capturedAt.localeCompare(a.capturedAt);
      return b[sortBy] - a[sortBy];
    });
    return result;
  }, [platformFilter, statusFilter, sortBy]);

  const unusedHighPriority = signals.filter((s) => s.status === 'unused' && s.score >= 80).length;

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Signals', value: signals.length, icon: Zap, color: 'text-accent' },
          { label: 'Used', value: signals.filter((s) => s.status === 'used').length, icon: CheckCircle2, color: 'text-positive' },
          { label: 'Unused', value: signals.filter((s) => s.status === 'unused').length, icon: Clock, color: 'text-muted-foreground' },
          { label: 'High Priority', value: unusedHighPriority, icon: TrendingUp, color: 'text-warning' },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={`stat-${stat.label}`} className="card-elevated px-4 py-3 flex items-center gap-3">
              <Icon size={16} className={stat.color} />
              <div>
                <p className="text-xl font-bold tabular-nums text-foreground">{stat.value}</p>
                <p className="text-2xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        {/* Platform tabs */}
        <div className="flex items-center gap-1 bg-secondary border border-border rounded-lg p-1">
          {platformTabs.map((tab) => (
            <button
              key={`platform-${tab.value}`}
              onClick={() => setPlatformFilter(tab.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                platformFilter === tab.value
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className={platformFilter === tab.value ? tab.color : ''}>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-1.5">
          {(['all', 'used', 'unused'] as StatusFilter[]).map((s) => (
            <button
              key={`status-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-150 capitalize ${
                statusFilter === s
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary border border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-muted-foreground">Sort:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            className="bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="score">Score</option>
            <option value="velocity24h">Velocity</option>
            <option value="postCount">Post Count</option>
            <option value="capturedAt">Captured At</option>
          </select>
        </div>
      </div>

      {/* Signal Cards Grid */}
      {filtered.length === 0 ? (
        <div className="card-elevated py-16 flex flex-col items-center gap-3">
          <Zap size={32} className="text-muted-foreground/30" />
          <p className="text-sm font-medium text-muted-foreground">No trend signals found</p>
          <p className="text-xs text-muted-foreground/60">
            Adjust your filters or wait for the next pipeline scan at 07:30 UTC.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
          {filtered.map((signal) => (
            <SignalCard
              key={signal.id}
              signal={signal}
              expanded={expandedId === signal.id}
              onToggleExpand={() =>
                setExpandedId((prev) => (prev === signal.id ? null : signal.id))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}